import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Plus, Trash2, UserPlus, UserMinus, ChevronDown, ChevronUp, Crown, ArrowLeft, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import Navigation from '../components/Navigation';
import { getAdminHeaders, canEdit } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAIXA_LABELS = { '8-9': '8 a 9 anos', '10': '10 anos', '11-12': '11 a 12 anos' };

const GruposDiscipulado = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [groupChildren, setGroupChildren] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [addChildOpen, setAddChildOpen] = useState(null);
  const [eligibleChildren, setEligibleChildren] = useState([]);
  const [newGroup, setNewGroup] = useState({ nome: '', faixa_etaria: '8-9', sexo: 'M', lider_ids: [] });
  const [transitionInfo, setTransitionInfo] = useState(null);
  const [volunteers, setVolunteers] = useState([]);

  useEffect(() => { fetchGroups(); fetchOptions(); fetchVolunteers(); }, []);

  const fetchOptions = async () => {
    try {
      const res = await axios.get(`${API}/groups/options`);
      setTransitionInfo(res.data);
    } catch {}
  };

  const fetchVolunteers = async () => {
    try {
      const res = await axios.get(`${API}/volunteers`);
      setVolunteers(res.data || []);
    } catch {}
  };

  useEffect(() => { fetchGroups(); fetchOptions(); fetchVolunteers(); }, []);

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API}/groups`);
      setGroups(res.data);
    } catch { toast.error('Erro ao carregar grupos'); }
    finally { setLoading(false); }
  };

  const toggleGroup = async (groupId) => {
    if (expandedGroup === groupId) {
      setExpandedGroup(null);
      return;
    }
    setExpandedGroup(groupId);
    if (!groupChildren[groupId]) {
      try {
        const res = await axios.get(`${API}/groups/${groupId}/children`);
        setGroupChildren((prev) => ({ ...prev, [groupId]: res.data }));
      } catch { toast.error('Erro ao carregar criancas'); }
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.nome) { toast.error('Informe o nome do grupo'); return; }
    try {
      await axios.post(`${API}/groups`, newGroup, { headers: getAdminHeaders() });
      toast.success('Grupo criado!');
      setCreateOpen(false);
      setNewGroup({ nome: '', faixa_etaria: '8-9', sexo: 'M', lider_ids: [] });
      fetchGroups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao criar grupo'); }
  };

  const openAddChild = async (group) => {
    setAddChildOpen(group.id);
    try {
      const res = await axios.get(`${API}/groups/eligible-children/${group.faixa_etaria}/${group.sexo}`);
      const currentIds = group.child_ids || [];
      setEligibleChildren(res.data.filter((c) => !currentIds.includes(c.id)));
    } catch { setEligibleChildren([]); }
  };

  const addChildToGroup = async (groupId, childId) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    const updatedIds = [...(group.child_ids || []), childId];
    try {
      await axios.put(`${API}/groups/${groupId}`, { child_ids: updatedIds }, { headers: getAdminHeaders() });
      toast.success('Crianca adicionada ao grupo!');
      setAddChildOpen(null);
      fetchGroups();
      setGroupChildren((prev) => ({ ...prev, [groupId]: null }));
      setExpandedGroup(null);
    } catch { toast.error('Erro ao adicionar'); }
  };

  const removeChildFromGroup = async (groupId, childId) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    const updatedIds = (group.child_ids || []).filter((id) => id !== childId);
    try {
      await axios.put(`${API}/groups/${groupId}`, { child_ids: updatedIds }, { headers: getAdminHeaders() });
      toast.success('Crianca removida do grupo');
      fetchGroups();
      setGroupChildren((prev) => ({ ...prev, [groupId]: (prev[groupId] || []).filter((c) => c.id !== childId) }));
    } catch { toast.error('Erro ao remover'); }
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    try {
      await axios.delete(`${API}/groups/${groupId}`, { headers: getAdminHeaders() });
      toast.success(`Grupo "${groupName}" removido!`);
      fetchGroups();
    } catch { toast.error('Erro ao excluir'); }
  };

  const getVolunteerName = (liderId) => {
    if (!liderId) return null;
    const vol = volunteers.find(v => v.id === liderId);
    return vol ? vol.nome : null;
  };

  const getLiderNames = (liderIds) => {
    if (!liderIds || liderIds.length === 0) return null;
    return liderIds.map(id => getVolunteerName(id)).filter(Boolean);
  };

  const toggleLider = (groupId, currentIds, volId) => {
    const ids = currentIds || [];
    const updated = ids.includes(volId) ? ids.filter(id => id !== volId) : [...ids, volId];
    changeLider(groupId, updated);
  };

  const toggleNewGroupLider = (volId) => {
    setNewGroup(p => {
      const ids = p.lider_ids || [];
      const updated = ids.includes(volId) ? ids.filter(id => id !== volId) : [...ids, volId];
      return { ...p, lider_ids: updated };
    });
  };

  const changeLider = async (groupId, liderIds) => {
    try {
      await axios.put(`${API}/groups/${groupId}`, { lider_ids: liderIds }, { headers: getAdminHeaders() });
      toast.success('Lideres atualizados!');
      fetchGroups();
    } catch { toast.error('Erro ao atualizar lideres'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <p className="font-inter text-gray-500">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <Users className="w-7 h-7" strokeWidth={2.5} />
              <h1 className="font-nunito font-bold text-2xl sm:text-3xl">Grupos de Discipulado</h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="btn-whatsapp-report"
                onClick={() => navigate('/relatorio-whatsapp')}
                className="bg-green-600 hover:bg-green-700 text-white rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all shadow-md"
              >
                <MessageCircle className="w-4 h-4" /> Relatório WhatsApp
              </button>
              {canEdit() && (
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <button data-testid="btn-create-group" className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all">
                    <Plus className="w-4 h-4" /> Novo Grupo
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-nunito font-bold text-2xl text-[#1A4D2E]">Criar Grupo</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div>
                      <Label className="font-inter font-semibold text-[#1A4D2E] mb-1 block">Nome do Grupo *</Label>
                      <Input data-testid="input-group-name" value={newGroup.nome} onChange={(e) => setNewGroup((p) => ({ ...p, nome: e.target.value }))} className="rounded-xl" placeholder="Ex: Meninos 8-9" />
                    </div>
                    <div>
                      <Label className="font-inter font-semibold text-[#1A4D2E] mb-1 block">Faixa Etaria</Label>
                      <select data-testid="input-group-faixa" value={newGroup.faixa_etaria} onChange={(e) => setNewGroup((p) => ({ ...p, faixa_etaria: e.target.value }))} className="w-full rounded-xl border border-gray-200 h-10 px-3 text-sm">
                        {Object.entries(FAIXA_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
                      </select>
                    </div>
                    <div>
                      <Label className="font-inter font-semibold text-[#1A4D2E] mb-1 block">Sexo</Label>
                      <select data-testid="input-group-sexo" value={newGroup.sexo} onChange={(e) => setNewGroup((p) => ({ ...p, sexo: e.target.value }))} className="w-full rounded-xl border border-gray-200 h-10 px-3 text-sm">
                        <option value="M">Masculino</option>
                        <option value="F">Feminino</option>
                      </select>
                    </div>
                    <div>
                      <Label className="font-inter font-semibold text-[#1A4D2E] mb-1 block">Líderes (Voluntários com área Líder GF)</Label>
                      <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-xl p-2 space-y-1">
                        {volunteers.filter(v => v.areas && v.areas.includes('lider_gf')).length === 0 && <p className="text-xs text-gray-400 p-1">Nenhum voluntário com área "Líder GF" cadastrado</p>}
                        {volunteers.filter(v => v.areas && v.areas.includes('lider_gf')).map(v => (
                          <label key={v.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#F2F7F4] cursor-pointer">
                            <input type="checkbox" checked={(newGroup.lider_ids || []).includes(v.id)} onChange={() => toggleNewGroupLider(v.id)} className="accent-[#1A4D2E]" />
                            <span className="text-sm font-inter">{v.nome}</span>
                            <Crown className="w-3 h-3 text-amber-500" />
                          </label>
                        ))}
                      </div>
                    </div>
                    <Button data-testid="submit-create-group" onClick={handleCreateGroup} className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-5">
                      Criar Grupo
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              )}
            </div>
          </div>
          <p className="font-inter text-white/70 text-sm">{groups.length} grupos criados</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Transition Window Info */}
        {transitionInfo && (
          <Card className={`rounded-xl p-4 border-2 ${transitionInfo.is_transition_window ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            <p className={`font-inter text-sm font-semibold ${transitionInfo.is_transition_window ? 'text-green-700' : 'text-amber-700'}`}>
              {transitionInfo.is_transition_window
                ? 'Janela de transicao ABERTA - As criancas podem mudar de grupo neste mes.'
                : `Fora da janela de transicao. Proxima janela: ${transitionInfo.next_window}. Criancas permanecem no grupo atual ate a proxima janela.`
              }
            </p>
            <p className="font-inter text-xs text-gray-500 mt-1">Transicoes permitidas apenas em Janeiro e Julho. Novos cadastros usam a idade atual.</p>
          </Card>
        )}
        {groups.length === 0 ? (
          <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="font-inter text-gray-500">Nenhum grupo criado ainda</p>
          </Card>
        ) : (
          groups.map((g) => (
            <Card key={g.id} className="bg-white rounded-xl shadow-sm border border-[#E1ECE5] overflow-hidden">
              <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => toggleGroup(g.id)} data-testid={`group-${g.id}`}>
                <div>
                  <h4 className="font-nunito font-bold text-lg text-[#1A4D2E]">{g.nome}</h4>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <span className="bg-[#F2F7F4] text-[#1A4D2E] px-2 py-0.5 rounded text-xs font-semibold">{FAIXA_LABELS[g.faixa_etaria] || g.faixa_etaria}</span>
                    <span className="bg-[#F2F7F4] text-[#1A4D2E] px-2 py-0.5 rounded text-xs font-semibold">{g.sexo === 'M' ? 'Masculino' : 'Feminino'}</span>
                    <span className="bg-[#FFF9E6] text-[#D4A517] px-2 py-0.5 rounded text-xs font-bold">{(g.child_ids || []).length} criancas</span>
                    {getLiderNames(g.lider_ids)?.length > 0 && getLiderNames(g.lider_ids).map((name, i) => (
                      <span key={i} className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1">
                        <Crown className="w-3 h-3" /> {name}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit() && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`delete-group-${g.id}`} onClick={(e) => e.stopPropagation()} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full p-2 transition-all">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir grupo</AlertDialogTitle>
                          <AlertDialogDescription>Tem certeza que deseja excluir o grupo <strong>{g.nome}</strong>?</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteGroup(g.id, g.nome)} className="bg-red-500 hover:bg-red-600">Excluir</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  {expandedGroup === g.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
              </div>

              {expandedGroup === g.id && (
                <div className="border-t border-[#E1ECE5] p-4 bg-[#FAFBFA]">
                  {canEdit() && (
                    <div className="mb-3">
                      <label className="font-inter text-sm font-semibold text-[#1A4D2E] flex items-center gap-1 mb-1">
                        <Crown className="w-4 h-4 text-amber-600" /> Líderes:
                      </label>
                      <div className="max-h-28 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-white space-y-1">
                        {volunteers.filter(v => v.areas && v.areas.includes('lider_gf')).length === 0 && <p className="text-xs text-gray-400 p-1">Nenhum voluntário com área "Líder GF" cadastrado</p>}
                        {volunteers.filter(v => v.areas && v.areas.includes('lider_gf')).map(v => (
                          <label key={v.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#F2F7F4] cursor-pointer">
                            <input type="checkbox" data-testid={`lider-check-${g.id}-${v.id}`} checked={(g.lider_ids || []).includes(v.id)} onChange={() => toggleLider(g.id, g.lider_ids, v.id)} className="accent-[#1A4D2E]" />
                            <span className="text-sm font-inter">{v.nome}</span>
                            <Crown className="w-3 h-3 text-amber-500" />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {canEdit() && (
                    <Dialog open={addChildOpen === g.id} onOpenChange={(open) => setAddChildOpen(open ? g.id : null)}>
                      <DialogTrigger asChild>
                        <button data-testid={`add-child-to-${g.id}`} onClick={() => openAddChild(g)} className="mb-3 flex items-center gap-1 text-[#1A4D2E] hover:bg-[#1A4D2E]/10 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all">
                          <UserPlus className="w-4 h-4" /> Adicionar Crianca
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle className="font-nunito font-bold text-xl text-[#1A4D2E]">Adicionar ao {g.nome}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-2 max-h-[400px] overflow-y-auto py-2">
                          {eligibleChildren.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">Nenhuma crianca elegivel encontrada para esta faixa etaria e sexo</p>
                          ) : (
                            eligibleChildren.map((c) => (
                              <button key={c.id} data-testid={`eligible-${c.id}`} onClick={() => addChildToGroup(g.id, c.id)} className="w-full text-left p-3 rounded-xl hover:bg-[#F2F7F4] border border-gray-100 transition-all flex items-center justify-between">
                                <div>
                                  <span className="font-semibold text-[#1A4D2E]">#{c.numero || 0} {c.nome}</span>
                                  <span className="text-gray-400 text-xs ml-2">{c.idade} anos</span>
                                </div>
                                <UserPlus className="w-4 h-4 text-[#1A4D2E]" />
                              </button>
                            ))
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}

                  {groupChildren[g.id] ? (
                    groupChildren[g.id].length > 0 ? (
                      <div className="space-y-2">
                        {groupChildren[g.id].map((c) => (
                          <div key={c.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-100">
                            <span className="font-inter text-sm text-[#1A4D2E] font-semibold">#{c.numero || 0} {c.nome} <span className="text-gray-400 font-normal">({c.idade} anos)</span></span>
                            {canEdit() && (
                              <button data-testid={`remove-child-${c.id}`} onClick={() => removeChildFromGroup(g.id, c.id)} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full p-1 transition-all">
                                <UserMinus className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-2">Nenhuma crianca neste grupo</p>
                    )
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-2">Carregando...</p>
                  )}
                </div>
              )}
            </Card>
          ))
        )}
      </div>
      <Navigation active="grupos" />
    </div>
  );
};

export default GruposDiscipulado;
